import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import {
  getIdToken,
  getUser,
  configureUserAuth,
  getValidUserIdToken,
  isTokenExpired,
  Identity,
} from "./auth";

interface AuthContextType {
  isAuthenticated: boolean;
  loading: boolean;
  user?: Identity;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Token buffer is 5 min before expiry, so checking every 4 min keeps the session alive.
const TOKEN_REFRESH_INTERVAL_MS = 4 * 60 * 1000;
// Matches enterprise-dashboards-ui: gives Amplify time to pick up the session seeded from Portal cookies.
const AUTH_CHECK_DELAY_MS = 2000;

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<Identity | undefined>();

  useEffect(() => {
    const checkAuth = async () => {
      try {
        configureUserAuth();
        const token = await getIdToken();
        const currentUser = await getUser();
        setUser(currentUser);
        setIsAuthenticated(!isTokenExpired(token));
      } catch (error) {
        console.error("Error fetching tokens", error);
        setIsAuthenticated(false);
      } finally {
        setLoading(false);
      }
    };

    const timeoutId = setTimeout(checkAuth, AUTH_CHECK_DELAY_MS);
    return () => clearTimeout(timeoutId);
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;

    const refreshToken = async () => {
      const token = await getValidUserIdToken();
      if (!token) {
        // Refresh token itself has expired, so the session is dead.
        setIsAuthenticated(false);
      }
    };

    const intervalId = setInterval(refreshToken, TOKEN_REFRESH_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [isAuthenticated]);

  return (
    <AuthContext.Provider value={{ isAuthenticated, loading, user }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export default AuthProvider;
